import React, {MutableRefObject, useContext, useEffect, useState} from 'react';
import {useTypedFastStorage} from '../../../hooks/useFastStorage';
import InlineInputControlRow from "../../../ui/layout/InlineInputControlRow";
import CheckBox from "../../../ui/CheckBox";
import PrimaryButton from "../../../ui/PrimaryButton";
import {ApplicableTableSettings} from "../../../tableSettings";
import SmartTable from "../../../uiControls/SmartTable";
import {
  DateRenderer,
  DateTimeRenderer,
  TextAlignLeftRenderer,
} from "../../../uiControls/SmartTableRenderers";
import {periodClosingReportTable} from "../tableSettings";
import Select, { SelectItem } from "../../../ui/Select";
import {formatDate, getCurrentMonth, getCurrentYear, nowDate} from "../../../dateUtils";
import { PeriodClosing } from '../../../fms.core/PeriodClosing';
import ApplicationContext from '../../../ApplicationContext';
import { JobNames } from '../../../framework.core/JobNames';
import _, { now } from 'lodash';
import moment from 'moment';
import { ApiResult, InsertResultsWithData } from '../../../framework.core/data/ApiResult';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import useGlobalYesNoDialog from '../../../hooks/useGlobalYesNoDialog';
import { SQLField, SQLInfo, SQLType } from '../../../framework.core/data/SQLInfo';
import { focusTabableByIndex } from '../../../util/inputUtils';
import { getFmsGlobal } from '../../../globals';
import Fieldset from '../../common/Fieldset';
import TextBox from '../../../ui/TextBox';

  let startDateYear = 0;
  let startDateMonth = 0;
  let dayList: Array<SelectItem>;
  let monthList: Array<SelectItem>;
  let yearList: Array<SelectItem>;
  let closingDate = '';

interface PeriodClosingStorageKeys {
  modelStorage: string,
};

interface PeriodClosingViewParams {
  id: string | undefined,
  onPopupClose?: () => void
};

interface PeriodClosingLoadedDetailsViewParams {
  saveClose: VoidFunction,
  saveReopen: VoidFunction,
  periodClosing: PeriodClosing,
  periodClosingRef: MutableRefObject<PeriodClosing | null>,
  setPeriodClosing: (x: Partial<PeriodClosing>) => void,
  onPopupClose?: () => void
};

function getPeriodClosingStorageKeys(): PeriodClosingStorageKeys {
  return {modelStorage: 'report-closing-period'}
}

function PeriodClosingImpl(params: PeriodClosingViewParams) {

  const {modelStorage} = getPeriodClosingStorageKeys();

  const [inStorage, setInStorage, removePeriodClosingFromStorage, periodClosingRef] = useTypedFastStorage<PeriodClosing>(modelStorage, null);
  const [initialized, setInitialized] = useState(false);

  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  const appContext = useContext(ApplicationContext);
  const acctOptions = appContext.suntekSettings?.accounting;
  const fmsGlobal = getFmsGlobal();

  useEffect(() => {
      initialize();
      return () => {
        removePeriodClosingFromStorage()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  async function checkDataAsync(): Promise<boolean> {
    if (inStorage?.endYearDate !== '') {
      if (moment(inStorage?.endYearDate).diff(closingDate, 'days') > 0) 
        return false;
    }
    return true;
  }

  async function doSave(isClosed: boolean) {
    let bQuestOPClose = false;

    if (!inStorage) {
      throw new Error('Invalid operation');
    }

    if (!await checkDataAsync()) {
      await showGlobalOkDialog(<p>To reopen the closing period, need to reverse Year End Processing.</p>);
      return false;
    }
    bQuestOPClose = isClosed;

    const result:ApiResult<boolean> = await appContext.api.closeAccount(closingDate, appContext.user?.id);

    if(!result || !result.success) return false;

    if (result.result) {
      if (bQuestOPClose) {
        const yes = await showGlobalYesNoDialog(<p>Do you want to OP Open & Accounting Open together ? </p>,false, 0, true);
        if(yes) {
          // jioyu - 251205 Period Close가 되면 Accounting Close가 자동으로 Open되도록 수정
          // jioyu - 260130 Period Close가 되면 Accounting Close가 풀리지 않고 유지되도록 변경
          const accountResult = await appContext.api.closeAcct(closingDate, appContext.user?.id, true, false, false);
          const opResult = await appContext.api.closeOperation(closingDate, appContext.user?.id, true, false, false);
          if (!opResult || !accountResult) return false;
          // if (!opResult) return false;
        }
        else {
          const opResult = await appContext.api.closeOperation(closingDate, appContext.user?.id, true, false, false, false);
          if (!opResult) return false;
        }
      }
      else {
        const opResult = await appContext.api.closeOperation(closingDate, appContext.user?.id, true, false, false);
        // const accountResult = await appContext.api.closeAcct(closingDate, appContext.user?.id, true, false, false);
        // if (!opResult || !accountResult) return false;
        if (!opResult) return false;
      }
    }

    if (!appContext.api.closeCheckAccount(closingDate, appContext.user?.id)) return false;

    let sqlInfoList: SQLInfo[] = [];
    sqlInfoList.push(new SQLInfo({
      tableName: "T_AOTHERINFO",
      type: SQLType.Insert,
      fields: [
        new SQLField({ fieldName: "F_TBNAME", propertyName: "TBNAME", value: "PERIOD"}),
        new SQLField({ fieldName: "F_D1", propertyName: "D1", value: "GETDATE()"}),
        new SQLField({ fieldName: "F_D2", propertyName: "D2", value: closingDate}),
        new SQLField({ fieldName: "F_C1", propertyName: "C1", value: appContext.user?.id}),
        new SQLField({ fieldName: "F_C2", propertyName: "C2", value: ""}),
      ],
      key: new SQLField({ fieldName: "F_Id", propertyName: "Id", value: 0})
    }));

    const apiResult:ApiResult<InsertResultsWithData> = await appContext.api.saveJobDetail(JobNames.PeriodClosing, sqlInfoList);

    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(<p>Failure to Close.</p>);
      return false;
    }
    else {
      await showGlobalOkDialog(<p>Successfully Done.</p>);
      if (appContext.suntekSettings?.myCompany) {
        appContext.setSuntekSettings({
          ...appContext.suntekSettings,
          myCompany:{
            ...appContext.suntekSettings?.myCompany,
            closeDate: closingDate,
            acctCloseBy: appContext.user?.id??''
          }
        });
      }
      fmsGlobal.CloseDate = closingDate;
      onPopupClose();
    }

    return true;
  }

  function saveReopen() {
    doSave(true);
  }

  function saveClose() {
    doSave(false);
  }

  function onPopupClose() {
    if (params?.onPopupClose) {
      params?.onPopupClose();
      removePeriodClosingFromStorage();
    }
  }

  async function loadExisting() { // PeriodClosing
    const apiResult = await appContext.api.getJobDetail(JobNames.PeriodClosing);

    if (!apiResult || !apiResult.success) {
      return;
    }

    dayList = [];
    monthList = [];
    yearList = [];

    // FaSetProcYear
    const model = new PeriodClosing(_.cloneDeep(apiResult.result));
    startDateYear = Number(acctOptions?.startDateYear);
    startDateMonth = Number(acctOptions?.startDateMonth);
    model.acctStartDate = moment(new Date(startDateYear, startDateMonth - 1, 1)).format("YYYY-MM-DDTHH:mm:ss");
    model.fiscalEndMonth = acctOptions?.fiscalEndMonth??12;
    const closeDate = await appContext.api.getFieldValueFromInitData("F_CloseDate");
    const mpWord = await appContext.api.getFieldValueFromInitData("F_MPWORD");
    model.lastCloseDate = closeDate > mpWord ? closeDate : mpWord;

    // FaSetInterface
    if (startDateMonth === 1) 
      yearList.push({label: `${startDateYear - 1}`, value : startDateYear - 1});
    for (let i = startDateYear; i <= getCurrentYear(); i++) {
      yearList.push({label: `${i}`, value : i});
    }

    if (getCurrentMonth() === 0) {
      model.currYear = getCurrentYear() - 1;
      model.currMonth = 12;
      model.currDay = 31;
    }
    else {
      model.currYear = getCurrentYear();
      model.currMonth = getCurrentMonth();
      model.currDay = new Date(model.currYear, model.currMonth, 0).getDate();
    }

    let nStartMonth, nEndMonth;
    if (startDateYear === model.currYear) {
      nStartMonth = startDateMonth === 1 ? 1 : startDateMonth - 1;
      nEndMonth = 12;
    }
    else if (moment(now()).year() === model.currYear) {
      nStartMonth = 1;
      nEndMonth = moment(now()).month() + 1;
    }
    else {
      nStartMonth = 1;
      nEndMonth = 12;
    }
    for (let i = nStartMonth; i <= nEndMonth; i++) {
        monthList.push({label: `${i}`, value : i});
    }
    model.currMonth = nEndMonth;

    let dDate = 0;
    if (moment(nowDate()).diff(new Date(model.currYear, model.currMonth - 1, model.currDay), 'days') <= 0)
     dDate = new Date(now()).getDate();
    else {
     dDate = new Date(model.currYear, model.currMonth, 0).getDate();
    }
    if ((model.currYear === startDateYear && model.currMonth < startDateMonth) || model.currYear < startDateYear)
      dayList.push({label: `${dDate}`, value : dDate});
    else {
      for (let i = 1; i <= dDate; i++) {
        dayList.push({label: `${i}`, value : i});
      }
    }
    model.currDay = dDate;

    closingDate = moment(new Date(model.currYear, model.currMonth - 1, model.currDay)).format("YYYY-MM-DDTHH:mm:ss");
    if (moment(model.lastCloseDate).diff(closingDate, 'days') < 0) 
      model.isClose = false;
    else 
      model.isClose = true;

    setInStorage(model);

    if (periodClosingRef.current) {
      periodClosingRef.current.trackable = true;
    }
  }

  async function initialize() {
    if (!inStorage) 
      await loadExisting();
    setInitialized(true);
  }

  if (!initialized || !inStorage) {
    return null;
  }

  return (
    <PeriodClosingInitializedImpl
      saveClose={saveClose}
      saveReopen= {saveReopen}
      periodClosing={inStorage}
      periodClosingRef={periodClosingRef}
      setPeriodClosing={setInStorage}
      onPopupClose={params.onPopupClose}
    />
  );
}

function IndicatorRenderer({row}: any) {
  return <div style={{textAlign: 'center', width: '100%'}}>{row.index + 1}</div>;
}

function PeriodClosingInitializedImpl({
                                        saveClose,
                                        saveReopen,
                                        periodClosing,
                                        periodClosingRef,
                                        setPeriodClosing,
                                        onPopupClose
                                      }: PeriodClosingLoadedDetailsViewParams) {
  useEffect(() => {
    focusTabableByIndex(0);
  }, []);

  function createMonthRange() {
    monthList = [];
    let nStartMonth, nEndMonth;
    if (startDateYear === periodClosing.currYear) {
      nStartMonth = startDateMonth === 1 ? 1 : startDateMonth - 1;
      nEndMonth = 12;
    }
    else if (startDateYear > periodClosing.currYear) {
      nStartMonth = 12;
      nEndMonth = 12;
    }
    else if (moment(now()).year() === periodClosing.currYear) {
      nStartMonth = 1;
      nEndMonth = moment(now()).month() + 1;
    }
    else {
      nStartMonth = 1;
      nEndMonth = 12;
    }
    for (let i = nStartMonth; i <= nEndMonth; i++) {
    monthList.push({label: `${i}`, value : i});
    }
    periodClosing.currMonth = nEndMonth;
  }

  function createDayRange() {
    dayList = [];
    let dDate = 0;
    if (moment(nowDate()).diff(new Date(periodClosing.currYear, periodClosing.currMonth - 1, periodClosing.currDay), 'days') <= 0)
    dDate = new Date(now()).getDate();
    else {
    dDate = new Date(periodClosing.currYear, periodClosing.currMonth, 0).getDate();
    }
    if ((periodClosing.currYear === startDateYear && periodClosing.currMonth < startDateMonth) || periodClosing.currYear < startDateYear)
      dayList.push({label: `${dDate}`, value : dDate});
    else {
      for (let i = 1; i <= dDate; i++) {
      dayList.push({label: `${i}`, value : i});
      }
    }
    periodClosing.currDay = dDate;
  }

  useEffect(() => {
    fetchReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function onChange(change: Partial<PeriodClosing>, dateIdx = 0) { // 1 = month, 2 = year
    setPeriodClosing(change);

    if (dateIdx === 1) {
      createDayRange();
    }
    else if (dateIdx === 2) {
      createMonthRange();
      createDayRange();
    }
    
    closingDate = moment(new Date(periodClosing.currYear, periodClosing.currMonth - 1, periodClosing.currDay)).format("YYYY-MM-DDTHH:mm:ss");
    if (moment(periodClosing.lastCloseDate).diff(closingDate, 'days') < 0) 
      periodClosing.isClose = false;
    else 
      periodClosing.isClose = true;
  }

  function fetchReport() {
    onChange({
    })
  }

  const tableOptions: ApplicableTableSettings = {
    tableName: 'periodClosingReportTable',
    ...periodClosingReportTable,
    data: (periodClosing.closingList ?? []).map((x) => ({...x})),
    rowRenderers: [
      {accessor: 'indicator', Cell: IndicatorRenderer},
      {accessor: 'date', Cell: DateTimeRenderer},
      {accessor: 'closedPeriod', Cell: DateRenderer},
      {accessor: 'userId', Cell: TextAlignLeftRenderer},
      {accessor: 'remark', Cell: TextAlignLeftRenderer},
    ],
  };

  return (
    <div className='report-popup'>
      <div className='report-row'>
      <Fieldset label="Closing Period">
          <div style={{marginLeft: '20px', width: '400px'}}>
            <InlineInputControlRow label="Closing Month/Day/Year" alignRight mode='w210'>
              <div style={{display: 'flex'}}>
                <Select 
                  name='period-closing-currMonth'
                  alignCenter
                  noPaddingLeft
                  value={periodClosing.currMonth}
                  onChange={(e) => onChange({currMonth: +e.target.value}, 1)} 
                  items={monthList}
                />
                <Select
                  name='period-closing-currDay' 
                  alignCenter
                  noPaddingLeft
                  value={periodClosing.currDay}
                  onChange={(e) => onChange({currDay: +e.target.value})}
                  items={dayList}
                />
                <Select
                  name='period-closing-currYear' 
                  alignCenter
                  noPaddingLeft
                  value={periodClosing.currYear}
                  onChange={(e) => onChange({currYear: +e.target.value}, 2)}
                  items={yearList}
                />
              </div>
            </InlineInputControlRow>
             <div style={{width:'300px'}}>
            <InlineInputControlRow label="Last Year End" alignRight smallMargin mode="w210">
             <TextBox name='period-colsing-lastYearEnd' disabled value={formatDate(periodClosing.endYearDate)} style={{textAlign: 'center'}} />
            </InlineInputControlRow>
            </div>
             <div style={{width:'300px'}}>
            <InlineInputControlRow label="Current Closed Period" alignRight smallMargin mode="w210">
              <TextBox name='period-colsing-currClosePeriod' disabled value={formatDate(periodClosing.lastCloseDate)} style={{textAlign: 'center'}} />
            </InlineInputControlRow>
            </div>
          </div>
        </Fieldset>
      </div>
      <div className='clearfix'></div>
      <div className='report-col-12'>
        {periodClosing.isClose ? (
        <div id='period-closing-closeText' style={{margin: '10px 0px', width: 490, fontWeight: 'bold', color: 'red'}}>
        ** This action will undo the closing of the Accounting Information by the period closing date.

      </div>
        ) : (
        <div id='period-closing-closeText' style={{margin: '10px 0px', width: 400, fontWeight: 'bold'}}>
        ** This action will finalize the Accounting Information by the Closing Date and any action such as addition, modification cannot be done.
      </div>
        )}
      </div>
      <SmartTable {...tableOptions} />
      <div style={{marginLeft: 80}}>
        <CheckBox
          label={(
            <span style={{color: 'red', fontWeight: 'bold'}}>
              {'Please check here to confirm Period Closing'}
            </span>
          )}
          focusId="customer-attachNote"
          value={periodClosing.canCreate}
          onChange={canCreate => onChange({canCreate: canCreate})}
        />
      </div>
      <div className='report-hr'>&nbsp;</div>
      <div className='report-footer'>
        {periodClosing.isClose ? (
          <PrimaryButton
            name = 'period-closing-reopen-button'
            focusable={true}
            size="regular"
            disabled={!periodClosing.canCreate}
            onClick={saveReopen}>
            REOPEN
          </PrimaryButton>
        ) : (
          <PrimaryButton
            name = 'period-closing-close-button'
            focusable={true}
            size="regular"
            disabled={!periodClosing.canCreate}
            onClick={saveClose}>
            Apply
          </PrimaryButton>
        )}
        <PrimaryButton
          name = 'period-closing-cancel-button'
          focusable={true}
          size="regular"
          onClick={onPopupClose}>
          Close
        </PrimaryButton>
      </div>
    </div>
  );
}

const PeriodClosingDetails = ({onPopupClose}: { onPopupClose?: () => void }) => {
  return (
    <PeriodClosingImpl id={''} onPopupClose={onPopupClose} key={`periodClosing-details`}/>
  );
};
export default PeriodClosingDetails;
